<template>
  <div class="payment-success-page">
    <div class="payment-container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="mt-3">Loading payment details...</p>
      </div>

      <!-- Success State -->
      <div v-else-if="paymentStatus === 'paid'" class="success-state">
        <div class="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="16 10 10.5 16 8 13"></polyline>
          </svg>
        </div>
        
        <h1 class="success-title">Payment Successful!</h1>
        <p class="success-message">Thank you for your payment.</p>
        
        <div class="payment-details" v-if="paymentInfo">
          <div class="detail-row">
            <span class="label">Practice:</span>
            <span class="value">{{ paymentInfo.SiteName }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Amount Paid:</span>
            <span class="value amount">${{ formatAmount(paymentInfo.TotalCharged) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Date:</span>
            <span class="value">{{ formatDate(paymentInfo.PaidAt) }}</span>
          </div>
          <div class="detail-row" v-if="paymentInfo.PatientName">
            <span class="label">Patient:</span>
            <span class="value">{{ paymentInfo.PatientName }}</span>
          </div>
        </div>

        <div class="receipt-note">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>A receipt will be sent to your email by Stripe.</span>
        </div>
      </div>

      <!-- Pending State (redirected before payment completed) -->
      <div v-else-if="paymentStatus === 'pending' || paymentStatus === 'sent'" class="pending-state">
        <div class="pending-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#ffc107" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        
        <h1 class="pending-title">Payment Processing</h1>
        <p class="pending-message">Your payment is being processed. This page will update automatically.</p>
        
        <div class="payment-details" v-if="paymentInfo">
          <div class="detail-row">
            <span class="label">Practice:</span>
            <span class="value">{{ paymentInfo.SiteName }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Amount Due:</span>
            <span class="value amount">${{ formatAmount(paymentInfo.TotalCharged) }}</span>
          </div>
        </div>
      </div>

      <!-- Failed State -->
      <div v-else-if="paymentStatus === 'failed'" class="failed-state">
        <div class="failed-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
        </div>
        
        <h1 class="failed-title">Payment Failed</h1>
        <p class="failed-message">Unfortunately, your payment could not be processed. Please contact the dental office for assistance.</p>
        
        <div class="payment-details" v-if="paymentInfo">
          <div class="detail-row">
            <span class="label">Practice:</span>
            <span class="value">{{ paymentInfo.SiteName }}</span>
          </div>
        </div>
      </div>

      <!-- Expired State -->
      <div v-else-if="paymentStatus === 'expired'" class="expired-state">
        <div class="expired-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#6c757d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </div>
        
        <h1 class="expired-title">Payment Link Expired</h1>
        <p class="expired-message">This payment link has expired. Please contact the dental office for a new payment link.</p>
      </div>

      <!-- Not Found State -->
      <div v-else-if="notFound" class="not-found-state">
        <div class="not-found-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#6c757d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        
        <h1 class="not-found-title">Payment Not Found</h1>
        <p class="not-found-message">We couldn't find this payment. Please check the link or contact the dental office.</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#dc3545" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        
        <h1 class="error-title">Something Went Wrong</h1>
        <p class="error-message">{{ error }}</p>
      </div>

      <PoweredByFooter />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PoweredByFooter from '@/components/PoweredByFooter.vue';

export default {
  name: 'PaymentSuccess',
  components: { PoweredByFooter },
  props: ['paymentId'],
  
  data() {
    return {
      loading: true,
      paymentInfo: null,
      paymentStatus: null,
      notFound: false,
      error: null,
      pollInterval: null
    };
  },

  created() {
    this.fetchPaymentStatus();
  },

  beforeDestroy() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  },

  methods: {
    async fetchPaymentStatus() {
      if (!this.paymentId) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      try {
        const apiUrl = process.env.VUE_APP_TEAMIO_API_URL;
        const response = await axios.get(`${apiUrl}/payment-status/${this.paymentId}`);
        
        if (response.data) {
          this.paymentInfo = response.data;
          this.paymentStatus = response.data.Status;
          
          // If still pending, poll for updates
          if (this.paymentStatus === 'pending' || this.paymentStatus === 'sent') {
            this.startPolling();
          }
        } else {
          this.notFound = true;
        }
      } catch (err) {
        console.error('Error fetching payment status:', err);
        if (err.response?.status === 404) {
          this.notFound = true;
        } else {
          this.error = 'Unable to load payment details. Please try again later.';
        }
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      // Poll every 3 seconds for status updates
      this.pollInterval = setInterval(async () => {
        try {
          const apiUrl = process.env.VUE_APP_TEAMIO_API_URL;
          const response = await axios.get(`${apiUrl}/payment-status/${this.paymentId}`);
          
          if (response.data) {
            this.paymentInfo = response.data;
            this.paymentStatus = response.data.Status;
            
            // Stop polling once we have a final status
            if (this.paymentStatus === 'paid' || this.paymentStatus === 'failed' || this.paymentStatus === 'expired') {
              clearInterval(this.pollInterval);
              this.pollInterval = null;
            }
          }
        } catch (err) {
          console.error('Error polling payment status:', err);
        }
      }, 3000);
    },

    formatAmount(amount) {
      if (!amount) return '0.00';
      return parseFloat(amount).toFixed(2);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.payment-success-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

.payment-container {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 480px;
  width: 100%;
  padding: 40px;
  text-align: center;
}

/* Loading State */
.loading-state {
  padding: 40px 0;
}

.loading-state p {
  color: #6c757d;
  margin-top: 16px;
}

/* Success State */
.success-state .success-icon {
  margin-bottom: 24px;
}

.success-title {
  color: #28a745;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.success-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Pending State */
.pending-state .pending-icon {
  margin-bottom: 24px;
}

.pending-title {
  color: #ffc107;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.pending-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Failed State */
.failed-state .failed-icon {
  margin-bottom: 24px;
}

.failed-title {
  color: #dc3545;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.failed-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Expired State */
.expired-state .expired-icon {
  margin-bottom: 24px;
}

.expired-title {
  color: #6c757d;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.expired-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Not Found State */
.not-found-state .not-found-icon {
  margin-bottom: 24px;
}

.not-found-title {
  color: #6c757d;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.not-found-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Error State */
.error-state .error-icon {
  margin-bottom: 24px;
}

.error-title {
  color: #dc3545;
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 8px;
}

.error-message {
  color: #6c757d;
  font-size: 16px;
  margin-bottom: 32px;
}

/* Payment Details */
.payment-details {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  color: #6c757d;
  font-size: 14px;
}

.detail-row .value {
  color: #212529;
  font-weight: 600;
  font-size: 14px;
}

.detail-row .value.amount {
  color: #28a745;
  font-size: 18px;
}

/* Receipt Note */
.receipt-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6c757d;
  font-size: 14px;
  padding: 16px;
  background: #e8f5e9;
  border-radius: 8px;
  margin-bottom: 24px;
}

/* Footer */
.payment-footer {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.powered-by {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #6c757d;
  font-size: 12px;
}

.stripe-logo {
  height: 20px;
  width: auto;
}

.teamio-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
}

.teamio-link:hover {
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .payment-container {
    padding: 24px;
  }

  .success-title,
  .pending-title,
  .failed-title,
  .expired-title,
  .not-found-title,
  .error-title {
    font-size: 24px;
  }
}
</style>
